import { Box, Title, UnstyledButton } from "@mantine/core";
import { IconArrowUp, IconCheck, IconLock, IconX } from "@tabler/icons-react";
import { useId } from "react";
import Button from "@/design-system/components/Button";
import type { SectorAdmin } from "../useSectorPermissions";
import { SectorAdminsLine } from "../SectorAdminsLine";
import { GatePreview } from "./GatePreview";
import { LOCKABLE_PAGE_COPY, type LockablePagePermission } from "./pageCopy";
import { buildPlanGateCopy } from "./planGateCopy";
import classes from "./index.module.css";

type FeatureGateBaseProps = {
  /** Página bloqueada: define o nome, a frase do bloco roxo e a prévia. */
  pageKey: LockablePagePermission;
  /**
   * Administradores do setor, mostrados pelo primeiro nome a quem não pode
   * resolver o bloqueio. Sem nenhum, a linha some.
   */
  admins: readonly SectorAdmin[];
  /**
   * Nome (do menu) da página para onde a saída leva: "Voltar para Contatos".
   * O DS escreve "Voltar para Conversas" fixo; o PermissionGate passa a última
   * página liberada em que a pessoa estava, e Conversas sem origem conhecida.
   */
  returnLabel: string;
  /** Saída do bloqueio: o X, o fundo e o botão de voltar. */
  onClose: () => void;
};

type PermissionGateReasonProps = FeatureGateBaseProps & {
  /**
   * Sem permissão. Quem não tem uma chave de página nunca é Admin do setor,
   * então a copy é sempre a de membro.
   */
  reason?: "perm";
};

type PlanGateReasonProps = FeatureGateBaseProps & {
  /** Fora do plano (SA-1401). */
  reason: "plan";
  /**
   * Admin da empresa (quem pode fazer o upgrade): copy de upgrade, "Voltar"
   * fantasma e o primário de upgrade, sem a linha de administradores.
   */
  isAdmin: boolean;
  /** Plano atual pelo nome de exibição; `null` quando não dá para nomear. */
  planNow: string | null;
  /** Plano mínimo com o recurso; `null` quando não há plano a oferecer. */
  planRequired: string | null;
  /** O que se libera no plano mínimo, na ordem de exibição. */
  unlocks: readonly string[];
  /** Primário de upgrade (só admin). */
  onPrimary: () => void;
  /**
   * Rótulo do primário: diz o que o clique faz (skill billing §7). "Ver planos
   * e fazer upgrade" (padrão) quando abre a troca de plano; "Falar com o
   * comercial" quando vai para Vendas (plano legado, conta sem assinatura).
   */
  primaryLabel?: string;
  /**
   * O primário do admin vai para o comercial: o corpo fala do comercial em vez
   * de "Faça o upgrade" (o rótulo vem em `primaryLabel`).
   */
  salesPath?: boolean;
  /** Primário em espera (resumo do billing carregando ou Portal abrindo). */
  primaryLoading?: boolean;
  /** Falha da ação do primário, mostrada no card acima dos botões. */
  primaryError?: string | null;
};

export type FeatureGateProps = PermissionGateReasonProps | PlanGateReasonProps;

type GateContent = {
  isPlan: boolean;
  eyebrow: string;
  headline: string;
  body: string;
  unlocksTitle: string | null;
  unlocks: readonly string[];
  showAdmins: boolean;
  onPrimary?: () => void;
  primaryLabel: string;
  primaryLoading: boolean;
  primaryError: string | null;
};

const DEFAULT_PLAN_PRIMARY_LABEL = "Ver planos e fazer upgrade";

function resolveContent(props: FeatureGateProps, label: string): GateContent {
  if (props.reason !== "plan") {
    return {
      isPlan: false,
      eyebrow: "Acesso restrito",
      headline: `Você não tem permissão para acessar ${label}`,
      body: `Seu acesso de membro não inclui ${label}. Você pode solicitar a liberação ao administrador da conta.`,
      unlocksTitle: null,
      unlocks: [],
      showAdmins: true,
      primaryLabel: DEFAULT_PLAN_PRIMARY_LABEL,
      primaryLoading: false,
      primaryError: null,
    };
  }

  const copy = buildPlanGateCopy({
    label,
    isAdmin: props.isAdmin,
    planNow: props.planNow,
    planRequired: props.planRequired,
    salesPath: props.salesPath,
  });
  return {
    isPlan: true,
    ...copy,
    unlocks: copy.unlocksTitle ? props.unlocks : [],
    showAdmins: !props.isAdmin,
    onPrimary: props.isAdmin ? props.onPrimary : undefined,
    primaryLabel: props.primaryLabel ?? DEFAULT_PLAN_PRIMARY_LABEL,
    primaryLoading: Boolean(props.isAdmin && props.primaryLoading),
    primaryError: (props.isAdmin && props.primaryError) || null,
  };
}

/**
 * Bloqueio de recurso do DS (docs/design-system/components/overlays/
 * FeatureGate.jsx), nos ramos de permissão e de plano. Onde o .jsx e o
 * protótipo Atendimento.dc.html divergem, vale o protótipo. Ocupa o container
 * do conteúdo do MainLayout, que precisa de `position: relative`. No celular
 * vira uma folha que preenche esse container (ver index.module.css).
 */
export function FeatureGate(props: FeatureGateProps) {
  const { pageKey, admins, returnLabel, onClose } = props;
  const titleId = useId();
  const unlocksTitleId = useId();
  const { label, pitch } = LOCKABLE_PAGE_COPY[pageKey];
  const content = resolveContent(props, label);
  const HeaderIcon = content.isPlan ? IconArrowUp : IconLock;

  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      data-reason={content.isPlan ? "plan" : "perm"}
      className={classes.root}>
      <GatePreview pageKey={pageKey} />
      {/* Clicar no fundo também sai (design); pelo teclado, o X e o botão. */}
      <Box
        className={classes.veil}
        onClick={onClose}
        aria-hidden
        data-testid="feature-gate-veil"
      />
      <Box className={classes.card}>
        <UnstyledButton
          className={classes.close}
          onClick={onClose}
          title="Fechar"
          aria-label="Fechar">
          <IconX
            size={15}
            stroke={2.3}
          />
        </UnstyledButton>
        <Box className={classes.header}>
          <Box
            component="span"
            className={classes.iconFrame}>
            <HeaderIcon
              size={18}
              stroke={2}
            />
          </Box>
          <Box
            component="span"
            className={classes.eyebrow}>
            {content.eyebrow}
          </Box>
        </Box>
        <Title
          order={1}
          id={titleId}
          fz="var(--sa-fs-dialog-title)"
          lh="var(--sa-lh-tight)"
          className={classes.title}>
          {content.headline}
        </Title>
        <Box className={classes.pitch}>
          <Box
            component="span"
            className={classes.pitchEyebrow}>
            O que essa funcionalidade faz
          </Box>
          <Box
            component="p"
            className={classes.pitchText}>
            {pitch}
          </Box>
        </Box>
        <Box
          component="p"
          className={classes.body}>
          {content.body}
        </Box>
        {content.unlocksTitle && content.unlocks.length > 0 && (
          <Box className={classes.unlocks}>
            <Box
              component="span"
              id={unlocksTitleId}
              className={classes.unlocksEyebrow}>
              {content.unlocksTitle}
            </Box>
            <Box
              component="ul"
              aria-labelledby={unlocksTitleId}
              className={classes.unlocksList}>
              {content.unlocks.map((unlock) => (
                <Box
                  component="li"
                  key={unlock}
                  className={classes.unlock}>
                  <Box
                    component="span"
                    className={classes.unlockCheck}
                    aria-hidden>
                    <IconCheck
                      size={15}
                      stroke={3}
                    />
                  </Box>
                  <span>{unlock}</span>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {content.showAdmins && (
          <SectorAdminsLine
            admins={admins}
            className={classes.admins}
          />
        )}
        {content.primaryError && (
          <Box
            component="p"
            role="alert"
            className={classes.primaryError}>
            {content.primaryError}
          </Box>
        )}
        <Box className={classes.actions}>
          {content.onPrimary ? (
            <>
              <Button
                variant="ghost"
                onClick={onClose}>
                Voltar
              </Button>
              <Button
                variant="primary"
                loading={content.primaryLoading}
                onClick={content.onPrimary}>
                {content.primaryLabel}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              onClick={onClose}>
              {`Voltar para ${returnLabel}`}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
